import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  AVAILABLE_PACKAGE_TYPES,
  AvailablePackageType,
  BuyOlxPackagesPayload,
  NormalizedAvailablePackages,
  PACKAGE_FEATURE_KEYS,
} from './buy-olx-packages-v2.interface';
import { Subject, takeUntil } from 'rxjs';
import {
  OlxAvailablePackage,
  OlxCategory,
  OlxLocationZone,
  PLANNER_OLX_ACTION,
  PLANNER_OLX_ACTIONS_REGISTRY,
} from '../../../../../modules/crm/power-olx-v2/interfaces/power-olx-v2.interface';
import { BaseService } from '../../../../../services/base.service';
import { PowerOlxV2Service } from '../../../../../modules/crm/power-olx-v2/services/power-olx-v2.service';
import { RequestHelper } from '../../../../../services/request-helper.service';
import { FormControl, Validators } from '@angular/forms';
import { SelectType } from '../../../../select/select.interface';
import { normalizeAvailablePackages, toNormalizedType } from './buy-olx-packages-v2.util';
import { DialogService } from '../../../../../services/dialog.service';
import { ModalData } from '../../../../../model/modal-data.model';
import { SidebarData, SidebarType } from '../../stacked-sidebar.interface';
import { PLANNER_SCOPE, PlannerSidebarData, PlannerSidebarMode } from '../planner-v2/planner-v2.interface';
import { StackedSidebarComponent } from '../../stacked-sidebar.component';
import { ModalService } from '../../../../../services/modal.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { PlannerService } from '../../../../../services/planner.service';

@Component({
  selector: 'app-buy-olx-packages-v2',
  templateUrl: './buy-olx-packages-v2.component.html',
  styleUrl: './buy-olx-packages-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BuyOlxPackagesV2Component implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject<void>();

  @Input() payload!: BuyOlxPackagesPayload;
  @Output() close = new EventEmitter<string>();

  private channelId!: string;
  protected preselectedPackage?: OlxAvailablePackage;
  protected plannerId?: string;
  protected categories: OlxCategory[] = [];
  protected zones: OlxLocationZone[] = [];
  protected totalPackagesCount: number = 0;
  protected packages: NormalizedAvailablePackages = { base: [], premium: [], mega: [] };
  protected selectedCategoryId = new FormControl<number | null>(null, Validators.required);
  protected selectedZoneId = new FormControl<string | null>(null);
  protected selectedBasePackage = new FormControl<OlxAvailablePackage | null>(null, Validators.required);
  protected selectedPremiumPackage = new FormControl<OlxAvailablePackage | null>(null, Validators.required);
  protected selectedMegaPackage = new FormControl<OlxAvailablePackage | null>(null, Validators.required);
  protected loaders = {
    categories: true,
    zones: false,
    packages: false,
    buy_packages: false,
  };

  constructor(
    private readonly powerOlxV2Service: PowerOlxV2Service,
    private readonly requestHelper: RequestHelper,
    private readonly dialogService: DialogService,
    private readonly modalService: ModalService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
    private readonly plannerService: PlannerService,
    protected readonly baseService: BaseService,
  ) {}

  ngOnInit(): void {
    this.initPayload();
    this.initCategories();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initPayload(): void {
    this.channelId = this.payload.channel_id;
    this.plannerId = this.payload.planner_id;

    this.preselectedPackage = this.payload.selected_package;

    if (this.preselectedPackage) {
      this.selectedCategoryId.setValue(this.preselectedPackage.category_id, { emitEvent: false });
    }
  }

  private initCategories(): void {
    this.requestHelper.handle<OlxCategory[]>(
      this.powerOlxV2Service.getCategories(),
      (res) => {
        this.categories = res;

        if (this.preselectedPackage) {
          this.selectedCategoryId.setValue(this.preselectedPackage.category_id);
        }
      },
      {
        key: 'power_olx_buy_packages_categories',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'categories',
        defaultErrorMessage: 'notifications_texts.error.failed_to_retrieve_categories',
      },
    );
  }

  protected onSelectCategory(): void {
    const categoryId = this.selectedCategoryId.value;

    if (!categoryId) return;

    this.onResetCategory();

    this.requestHelper.handle<OlxLocationZone[]>(
      this.powerOlxV2Service.getAvailableLocationZones(this.channelId, categoryId),
      (res) => {
        this.zones = res;

        if (this.zones.length) {
          this.selectedZoneId.setValidators(Validators.required);

          if (this.preselectedPackage?.zone_id) {
            const exists = this.zones.some((z) => z.id === this.preselectedPackage!.zone_id);

            if (exists) {
              this.selectedZoneId.setValue(this.preselectedPackage.zone_id, { emitEvent: false });
              this.getPackages();
            }
          }
        } else {
          this.getPackages();
        }
      },
      {
        key: 'power_olx_buy_packages_zones',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'zones',
        defaultErrorMessage: 'notifications_texts.error.failed_to_retrieve_zones',
        ignoreErrorCodes: ['PACKET_NOT_FOUND_FOR_CATEGORY'],
      },
    );
  }

  private onResetCategory(): void {
    this.zones = [];
    this.selectedZoneId.clearValidators();
    this.selectedZoneId.reset();
    this.onResetZone();
  }

  protected onSelectZone(): void {
    this.onResetZone();
    this.getPackages();
  }

  protected getPackages(): void {
    const categoryId = this.selectedCategoryId.value;

    if (!categoryId) return;

    this.requestHelper.handle<OlxAvailablePackage[]>(
      this.powerOlxV2Service.getAvailablePackages(this.channelId, categoryId, this.selectedZoneId.value),
      (res) => {
        this.totalPackagesCount = res.length;
        this.packages = normalizeAvailablePackages(res);

        if (this.preselectedPackage) {
          const type = toNormalizedType(this.preselectedPackage);
          const matched = this.packages[type].find((p) => p.variant_id === this.preselectedPackage!.variant_id);

          if (matched) {
            this.controlByType(type).setValue(matched, { emitEvent: false });
            this.setDefaultSelections(true);
            return;
          }
        }

        this.setDefaultSelections();
      },
      {
        key: 'power_olx_buy_packages_packages',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'packages',
        defaultErrorMessage: 'notifications_texts.error.failed_to_retrieve_packages',
        ignoreErrorCodes: ['PACKET_NOT_FOUND_FOR_CATEGORY'],
      },
    );
  }

  private onResetZone(): void {
    this.packages = { base: [], premium: [], mega: [] };
    this.totalPackagesCount = 0;
    [this.selectedBasePackage, this.selectedPremiumPackage, this.selectedMegaPackage].forEach((c) => {
      c.reset();
    });
  }

  private setDefaultSelections(skipIfHasValue = false): void {
    const setIf = (ctrl: FormControl<OlxAvailablePackage | null>, value: OlxAvailablePackage | null) => {
      if (skipIfHasValue && ctrl.value) return;
      ctrl.setValue(value);
    };

    setIf(this.selectedBasePackage, this.packages.base[0] ?? null);
    setIf(this.selectedPremiumPackage, this.packages.premium[0] ?? null);
    setIf(this.selectedMegaPackage, this.packages.mega[0] ?? null);
  }

  get packageCardsToShow(): { type: AvailablePackageType; items: OlxAvailablePackage[] }[] {
    return AVAILABLE_PACKAGE_TYPES.filter((t) => this.packages[t].length > 0).map((t) => ({
      type: t,
      items: this.packages[t],
    }));
  }

  protected controlByType(t: AvailablePackageType): FormControl<OlxAvailablePackage | null> {
    switch (t) {
      case AvailablePackageType.BASE:
        return this.selectedBasePackage;
      case AvailablePackageType.PREMIUM:
        return this.selectedPremiumPackage;
      case AvailablePackageType.MEGA:
        return this.selectedMegaPackage;
    }
  }

  protected canBuyByType(t: AvailablePackageType): boolean {
    const ctrl = this.controlByType(t);
    return !!this.packages[t].length && ctrl.enabled && ctrl.valid && !!ctrl.value;
  }

  protected onBuyType(t: AvailablePackageType): void {
    const ctrl = this.controlByType(t);
    const selected = ctrl.value;

    if (!selected) return;

    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.buy_package',
      price: selected.price,
    });

    this.loaders.buy_packages = true;
    dialogRef.subscribe((result) => {
      this.loaders.buy_packages = false;

      if (!result) return;

      this.requestHelper.handle<void>(
        this.powerOlxV2Service.purchasePackage(this.channelId, selected),
        () => {
          this.notificationService.showSuccess(
            this.translate.instant('notifications_texts.success.success'),
            this.translate.instant('notifications_texts.success.successfully_bought'),
          );
          this.onCloseSidebar('success');
        },
        {
          key: 'power_olx_buy_packages',
          destroy$: this.destroy$,
          loaderRef: this.loaders,
          loaderKey: 'buy_packages',
          defaultErrorMessage: 'notifications_texts.error.failed_to_buy_package',
        },
      );
    });
  }

  protected onUpdatePlanner(t: AvailablePackageType): void {
    const ctrl = this.controlByType(t);
    const selected = ctrl.value;

    if (!selected || !this.plannerId) return;

    this.requestHelper.handle<void>(
      this.plannerService.updatePlannerPayload(this.plannerId, {
        channel_id: this.channelId,
        selected_package: selected,
      }),
      () => {
        this.notificationService.showSuccess(
          this.translate.instant('notifications_texts.success.success'),
          this.translate.instant('notifications_texts.success.successfully_updated'),
        );
        this.onCloseSidebar('success');
      },
      {
        key: 'power_olx_buy_packages',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'buy_packages',
        defaultErrorMessage: 'notifications_texts.error.failed_to_update',
      },
    );
  }

  protected onOpenPlanner(t: AvailablePackageType): void {
    const ctrl = this.controlByType(t);
    const selected = ctrl.value;

    if (!selected) return;

    const modalData: ModalData<SidebarData<PlannerSidebarData>> = {
      component: StackedSidebarComponent,
      width: '50vw',
      hideHeader: true,
      data: {
        type: SidebarType.Planner,
        payload: {
          mode: PlannerSidebarMode.Create,
          scope: PLANNER_SCOPE.OLX,
          actions: [PLANNER_OLX_ACTIONS_REGISTRY[PLANNER_OLX_ACTION.BUY_OLX_PACKAGES]],
          payloads: [
            {
              channel_id: this.channelId,
              selected_package: selected,
            },
          ],
        },
      },
    };

    this.loaders.buy_packages = true;
    this.modalService
      .openModal(modalData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.loaders.buy_packages = false;

        if (res !== 'success') return;

        this.onCloseSidebar(res);
      });
  }

  protected isUpdateSelected(type: AvailablePackageType, selectedPackage: OlxAvailablePackage | null): boolean {
    return !!(
      this.preselectedPackage &&
      selectedPackage &&
      this.preselectedPackage.package_id === selectedPackage.package_id &&
      toNormalizedType(this.preselectedPackage) === type
    );
  }

  protected featuresByType(t: AvailablePackageType): string[] {
    return PACKAGE_FEATURE_KEYS[t] ?? [];
  }

  protected onCloseSidebar(status?: string): void {
    this.close.emit(status);
  }

  protected readonly SelectType = SelectType;
  protected readonly AvailablePackageType = AvailablePackageType;
}
